import $ from "jquery"
import * as _ from "./util"
import { isEmoji } from "./validate"


let hoverCounter = Date.now()
/**
 * DOM manipulations helper
 */
export default class Dom {
  static deep: number = 0
  /**
   * Check if passed tag has no closed tag
   *
   * @param {HTMLElement} tag - element to check
   * @returns {boolean}
   */
  public static isSingleTag(tag: HTMLElement): boolean {
    return (
      tag.tagName &&
      [
        "AREA",
        "BASE",
        "BR",
        "COL",
        "COMMAND",
        "EMBED",
        "HR",
        "IMG",
        "INPUT",
        "KEYGEN",
        "LINK",
        "META",
        "PARAM",
        "SOURCE",
        "TRACK",
        "WBR",
      ].includes(tag.tagName)
    )
  }

  /**
   * Check if element is BR or WBR
   *
   * @param {HTMLElement} element - element to check
   * @returns {boolean}
   */
  public static isLineBreakTag(element: HTMLElement): element is HTMLBRElement {
    return element && element.tagName && ["BR", "WBR"].includes(element.tagName)
  }

  /**
   * Helper for making Elements with classname and attributes
   *
   * @param  {string} tagName - new Element tag name
   * @param  {string[]|string} [classNames] - list or name of CSS classname(s)
   * @param  {object} [attributes] - any attributes
   *
   * @returns {HTMLElement}
   */
  public static make(
    tagName: string,
    classNames: string | string[] = null,
    attributes: object = {}
  ): HTMLElement {
    const el = document.createElement(tagName)

    if (Array.isArray(classNames)) {
      el.classList.add(...classNames)
    } else if (classNames) {
      el.classList.add(classNames)
    }

    for (const attrName in attributes) {
      if (Object.prototype.hasOwnProperty.call(attributes, attrName)) {
        el[attrName] = attributes[attrName]
      }
    }

    return el
  }

  /**
   * Helper for making Elements with classname and attributes
   *
   * @param  {HTMLElement} el - element
   * @param  {object} [dataset] - list or name of CSS classname(s)
   *
   * @returns {HTMLElement}
   */
  public static dataset(el: HTMLElement, dataset = {}): HTMLElement {
    for (const key in dataset) {
      el.dataset[key] = dataset[key]
    }
    return el
  }

  /**
   * Creates Text Node with the passed content
   *
   * @param {string} content - text content
   *
   * @returns {Text}
   */
  public static text(content: string): Text {
    return document.createTextNode(content)
  }

  /**
   * Creates SVG icon linked to the sprite
   *
   * @param {string} name - name (id) of icon from sprite
   * @param {number} [width] - icon width
   * @param {number} [height] - icon height
   *
   * @returns {SVGElement}
   */
  public static svg(name: string, width = 14, height = 14): SVGElement {
    const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg")

    icon.classList.add("icon", "icon--" + name)
    icon.setAttribute("width", width + "px")
    icon.setAttribute("height", height + "px")
    icon.innerHTML = `<use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#${name}"></use>`

    return icon
  }

  /**
   * Append one or several elements to the parent
   *
   * @param  {Element|DocumentFragment} parent - where to append
   * @param  {Element|Element[]|DocumentFragment|Text|Text[]} elements - element or elements list
   */
  public static append(
    parent: Element | DocumentFragment,
    elements: Element | Element[] | DocumentFragment | Text | Text[]
  ): void {
    if (Array.isArray(elements)) {
      elements.forEach((el) => parent.appendChild(el))
    } else {
      parent.appendChild(elements)
    }
  }

  /**
   * Append element or a couple to the beginning of the parent elements
   *
   * @param {Element} parent - where to append
   * @param {Element|Element[]} elements - element or elements list
   */
  public static prepend(parent: Element, elements: Element | Element[]): void {
    if (Array.isArray(elements)) {
      elements = elements.reverse()
      elements.forEach((el) => parent.prepend(el))
    } else {
      parent.prepend(elements)
    }
  }

  /**
   * Swap two elements in parent
   *
   * @param {HTMLElement} el1 - from
   * @param {HTMLElement} el2 - to
   * @deprecated
   */
  public static swap(el1: HTMLElement, el2: HTMLElement): void {
    // create marker element and insert it where el1 is
    const temp = document.createElement("div"),
      parent = el1.parentNode

    parent.insertBefore(temp, el1)

    // move el1 to right before el2
    parent.insertBefore(el1, el2)

    // move el2 to right before where el1 used to be
    parent.insertBefore(el2, temp)

    // remove temporary marker node
    parent.removeChild(temp)
  }

  /**
   * Selector Decorator
   *
   * Returns first match
   *
   * @param {Element} el - element we searching inside. Default - DOM Document
   * @param {string} selector - searching string
   *
   * @returns {Element}
   */
  public static find(
    el: Element | Document = document,
    selector: string
  ): Element {
    return el.querySelector(selector)
  }

  /**
   * Get Element by Id
   *
   * @param {string} id - id to find
   * @returns {HTMLElement | null}
   */
  public static get(id: string): HTMLElement | null {
    return document.getElementById(id)
  }

  /**
   * Selector Decorator.
   *
   * Returns all matches
   *
   * @param {Element|Document} el - element we searching inside. Default - DOM Document
   * @param {string} selector - searching string
   *
   * @returns {NodeList}
   */
  public static findAll(
    el: Element | Document = document,
    selector: string
  ): NodeList {
    return el.querySelectorAll(selector)
  }

  /**
   * Returns CSS selector for all text inputs
   */
  public static get allInputsSelector(): string {
    const allowedInputTypes = [
      "text",
      "password",
      "email",
      "number",
      "search",
      "tel",
      "url",
    ]

    return (
      "[contenteditable=true], textarea, input:not([type]), " +
      allowedInputTypes.map((type) => `input[type="${type}"]`).join(", ")
    )
  }

  /**
   * Find all contendeditable, textarea and editable input elements passed holder contains
   *
   * @param holder - element where to find inputs
   */
  public static findAllInputs(holder: Element): HTMLElement[] {
    return (
      _.array(holder.querySelectorAll(Dom.allInputsSelector))
        /**
         * If contenteditable element contains block elements, treat them as inputs.
         */
        .reduce((result, input) => {
          if (
            Dom.isNativeInput(input) ||
            Dom.containsOnlyInlineElements(input)
          ) {
            return [...result, input]
          }

          return [...result, ...Dom.getDeepestBlockElements(input)]
        }, [])
    )
  }

  /**
   * Search for deepest node which is Leaf.
   * Leaf is the vertex that doesn't have any child nodes
   *
   * @description Method recursively goes throw the all Node until it finds the Leaf
   *
   * @param {Node} node - root Node. From this vertex we start Deep-first search
   *                      {@link https://en.wikipedia.org/wiki/Depth-first_search}
   * @param {boolean} [atLast] - find last text node
   *
   * @returns {Node} - it can be text Node or Element Node, so that caret will able to work with it
   */
  public static getDeepestNode(node: Node, atLast = false): Node {
    /**
     * Current function have two directions:
     *  - starts from first child and every time gets first or nextSibling in special cases
     *  - starts from last child and gets last or previousSibling
     *
     * @type {string}
     */
    const child = atLast ? "lastChild" : "firstChild",
      sibling = atLast ? "previousSibling" : "nextSibling"

    if (node && node.nodeType === Node.ELEMENT_NODE && node[child]) {
      let nodeChild = node[child] as Node

      /**
       * special case when child is single tag that can't contain any content
       */
      if (
        Dom.isSingleTag(nodeChild as HTMLElement) &&
        !Dom.isNativeInput(nodeChild) &&
        !Dom.isLineBreakTag(nodeChild as HTMLElement)
      ) {
        /**
         * 1) We need to check the next sibling. If it is Node Element then continue searching for deepest
         * from sibling
         *
         * 2) If single tag's next sibling is null, then go back to parent and check his sibling
         * In case of Node Element continue searching
         *
         * 3) If none of conditions above happened return parent Node Element
         */
        if (nodeChild[sibling]) {
          nodeChild = nodeChild[sibling]
        } else if (nodeChild.parentNode[sibling]) {
          nodeChild = nodeChild.parentNode[sibling]
        } else {
          return nodeChild.parentNode
        }
      }

      return this.getDeepestNode(nodeChild, atLast)
    }

    return node
  }

  /**
   * Check if object is DOM node
   *
   * @param {*} node - object to check
   *
   * @returns {boolean}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static isElement(node: any): node is Element {
    if (_.isNumber(node)) {
      return false
    }

    return node && node.nodeType && node.nodeType === Node.ELEMENT_NODE
  }

  /**
   * Check if object is DocumentFragment node
   *
   * @param {object} node - object to check
   * @returns {boolean}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static isFragment(node: any): node is DocumentFragment {
    if (_.isNumber(node)) {
      return false
    }

    return (
      node && node.nodeType && node.nodeType === Node.DOCUMENT_FRAGMENT_NODE
    )
  }

  /**
   * Check if passed element is contenteditable
   *
   * @param {HTMLElement} element - html element to check
   *
   * @returns {boolean}
   */
  public static isContentEditable(element: HTMLElement): boolean {
    return element.contentEditable === "true"
  }

  /**
   * Checks target if it is native input
   *
   * @param {*} target - HTML element or string
   *
   * @returns {boolean}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static isNativeInput(
    target: any
  ): target is HTMLInputElement | HTMLTextAreaElement {
    const nativeInputs = ["INPUT", "TEXTAREA"]

    return target && target.tagName
      ? nativeInputs.includes(target.tagName)
      : false
  }

  /**
   * Checks if we can set caret
   *
   * @param {HTMLElement} target - target to check
   *
   * @returns {boolean}
   */
  public static canSetCaret(target: HTMLElement): boolean {
    let result = true

    if (Dom.isNativeInput(target)) {
      switch (target.type) {
        case "file":
        case "checkbox":
        case "radio":
        case "hidden":
        case "submit":
        case "button":
        case "image":
        case "reset":
          result = false
          break
      }
    } else {
      result = Dom.isContentEditable(target)
    }

    return result
  }

  /**
   * Checks node if it is empty
   *
   * @description Method checks simple Node without any childs for emptiness
   * If you have Node with 2 or more children id depth, you better use {@link Dom#isEmpty} method
   *
   * @param {Node} node - node to check
   *
   * @returns {boolean} true if it is empty
   */
  public static isNodeEmpty(node: Node): boolean {
    let nodeText

    if (
      this.isSingleTag(node as HTMLElement) &&
      !this.isLineBreakTag(node as HTMLElement)
    ) {
      return false
    }

    if (this.isElement(node) && this.isNativeInput(node)) {
      nodeText = (node as HTMLInputElement).value
    } else {
      nodeText = node.textContent.replace("\u200B", "")
    }

    return nodeText.trim().length === 0
  }

  /**
   * checks node if it is doesn't have any child nodes
   *
   * @param {Node} node - node to check
   *
   * @returns {boolean}
   */
  public static isLeaf(node: Node): boolean {
    if (!node) {
      return false
    }

    return node.childNodes.length === 0
  }

  /**
   * breadth-first search (BFS)
   * {@link https://en.wikipedia.org/wiki/Breadth-first_search}
   *
   * @description Pushes to stack all DOM leafs and checks for emptiness
   *
   * @param {Node} node - node to check
   * @returns {boolean}
   */
  public static isEmpty(node: Node): boolean {
    /**
     * Normalize node to merge several text nodes to one to reduce tree walker iterations
     */
    node.normalize()

    const treeWalker = [node]

    while (treeWalker.length > 0) {
      node = treeWalker.shift()

      if (!node) {
        continue
      }

      if (this.isLeaf(node) && !this.isNodeEmpty(node)) {
        return false
      }

      if (node.childNodes) {
        treeWalker.push(...Array.from(node.childNodes))
      }
    }

    return true
  }

  /**
   * Check if string contains html elements
   *
   * @param {string} str - string to check
   *
   * @returns {boolean}
   */
  public static isHTMLString(str: string): boolean {
    const wrapper = Dom.make("div")

    wrapper.innerHTML = str

    return wrapper.childElementCount > 0
  }

  /**
   * Return length of node`s text content
   *
   * @param {Node} node - node with content
   *
   * @returns {number}
   */
  public static getContentLength(node: Node): number {
    if (Dom.isNativeInput(node)) {
      return (node as HTMLInputElement).value.length
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return (node as Text).length
    }

    return node.textContent.length
  }

  /**
   * Return array of names of block html elements
   *
   * @returns {string[]}
   */
  public static get blockElements(): string[] {
    return [
      "address",
      "article",
      "aside",
      "blockquote",
      "canvas",
      "div",
      "dl",
      "dt",
      "fieldset",
      "figcaption",
      "figure",
      "footer",
      "form",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "header",
      "hgroup",
      "hr",
      "li",
      "main",
      "nav",
      "noscript",
      "ol",
      "output",
      "p",
      "pre",
      "ruby",
      "section",
      "table",
      "tr",
      "tfoot",
      "ul",
      "video",
    ]
  }

  /**
   * Check if passed content includes only inline elements
   *
   * @param {string|HTMLElement} data - element or html string
   *
   * @returns {boolean}
   */
  public static containsOnlyInlineElements(
    data: string | HTMLElement
  ): boolean {
    let wrapper: HTMLElement

    if (_.isString(data)) {
      wrapper = document.createElement("div")
      wrapper.innerHTML = data
    } else {
      wrapper = data
    }

    const check = (element: HTMLElement): boolean => {
      return (
        !Dom.blockElements.includes(element.tagName.toLowerCase()) &&
        Array.from(element.children).every(check)
      )
    }

    return Array.from(wrapper.children).every(check)
  }

  /**
   * Find and return all block elements in the passed parent (including subtree)
   *
   * @param {HTMLElement} parent - root element
   *
   * @returns {HTMLElement[]}
   */
  public static getDeepestBlockElements(parent: HTMLElement): HTMLElement[] {
    if (Dom.containsOnlyInlineElements(parent)) {
      return [parent]
    }

    return Array.from(parent.children).reduce((result, element) => {
      return [...result, ...Dom.getDeepestBlockElements(element as HTMLElement)]
    }, [])
  }

  /**
   * Helper for get holder from {string} or return HTMLElement
   *
   * @param {string | HTMLElement} element - holder's id or holder's HTML Element
   *
   * @returns {HTMLElement}
   */
  public static getHolder(element: string | HTMLElement): HTMLElement {
    if (_.isString(element)) {
      return document.getElementById(element)
    }

    return element
  }

  /**
   * Method checks passed Node if it is some extension Node
   *
   * @param {Node} node - any node
   *
   * @returns {boolean}
   */
  public static isExtensionNode(node: Node): boolean {
    const extensions = ["GRAMMARLY-EXTENSION"]

    return node && extensions.includes(node.nodeName)
  }

  /**
   * Returns true if element is anchor (is A tag)
   *
   * @param {Element} element - element to check
   *
   * @returns {boolean}
   */
  public static isAnchor(element: Element): element is HTMLAnchorElement {
    return element.tagName.toLowerCase() === "a"
  }
  public static getChildNodesAst(el: any, deep: number = this.deep, list = []) {
    // let list: any[] = clist;
    let caret:any=-1
    if(list.length==0 && deep==0){
      this.deep = 0
    }
    let childNodes = el.childNodes
    for (let i = 0, len = childNodes.length; i < len; i++) {
      let node = childNodes[i]
      // emoji 过滤掉
      if (isEmoji(node.textContent.trim())) continue
      switch (node.nodeName) {
        case "#text":
          if (node.textContent.length>0) {
            if(/[\n\r]/.test(node.textContent)){
             let text = node.textContent.split(/[\n\r]/g)
             text.forEach((ele,idx)=>{
              list.push({
                $index: i,
                nodeType: node.nodeType,
                text: ele,
                $deep: deep + idx,
              })
             })
             this.deep=deep + text.length - 1
          
            }else{
              list.push({
                $index: i,
                nodeType: node.nodeType,
                text: node.textContent,
                $deep: deep,
              })
            }
            
          }

          break
        case "SPAN":
          let spanObj: any = {}
          if(caret==-1&&node.className=="oldcaret"){
            list.push("caret")
	          caret=1
          }
          if(node.className.indexOf("ignore")!=-1&&node.className.indexOf("lianduStart")!=-1){
            let id=node.dataset.continuousid
            spanObj = {
              $index: i,
              nodeType: node.nodeType,
              wordType: "lianduStart",
              word: node.textContent.trim() || " ",
              isTag: true,
              $deep: deep,
              isPlaceHolder: 1,
              continuousChange: true,
              continuousStart: true,
              continuousId:id,
              time:id
            }
            list.push(spanObj)
        
          }else if(node.className.indexOf("ignore")!=-1&&node.className.indexOf("lianduEnd")!=-1){
            let id=node.dataset.continuousid
            spanObj = {
              $index: i,
              nodeType: node.nodeType,
              wordType: "lianduEnd",
              word: node.textContent.trim() || " ",
              isTag: true,
              $deep: deep,
              isPlaceHolder: 1,
              continuousChange: true,
              continuousEnd: true,
              continuousId:id,
              time:id
            }
            list.push(spanObj)
          }else if(node.className.indexOf("ignore")!=-1&&node.className.indexOf("localSpeedStart")!=-1){
            let speedHover=node.dataset.speedhover
            let speed=node.dataset.speed
            spanObj = {
              $index: i,
              nodeType: node.nodeType,
              wordType: "localSpeedStart",
              word: node.textContent.trim() || " ",
              isTag: true,
              $deep: deep,
              isPlaceHolder: 1,
              startSpeed: true,
              speed:speed,
              speedHover:speedHover
            }
            list.push(spanObj)
          }else if(node.className.indexOf("ignore")!=-1&&node.className.indexOf("localSpeedEnd")!=-1){
            let speedHover=node.dataset.speedhover
            let speed=node.dataset.speed
            spanObj = {
              $index: i,
              nodeType: node.nodeType,
              wordType: "localSpeedEnd",
              word: node.textContent || " ",
              isTag: true,
              $deep: deep,
              isPlaceHolder: 1,
              endSpeed: true,
              speed:speed,
              speedHover:speedHover
            }
            list.push(spanObj)
          } else if (node.className.indexOf("ignore") != -1 && node.className.indexOf("multiSpeakerStart") != -1) {
            let speakerHover = +node.dataset.hover
            let speed = +node.dataset.speed
            let pitch = +node.dataset.pitch
            let gender = node.dataset.gender
            let speaker = node.dataset.speaker
            let speakerName = node.dataset.speakername
            let speakerSource = node.dataset.speakersource
            spanObj = {
              $index: i,
              nodeType: node.nodeType,
              wordType: "multiSpeakerStart",
              word: node.textContent.trim() || " ",
              isTag: true,
              $deep: deep,
              isPlaceHolder: 1,
              startSpeaker: true,
              speed: speed,
              pitch: pitch,
              hover: speakerHover,
              gender: gender,
              speaker: speaker,
              speakerName: speakerName,
              speakerSource: speakerSource,
              emotionWeight: node.dataset.emotionweight || 1
            }
            list.push(spanObj)
          } else if (node.className.indexOf("ignore") != -1 && node.className.indexOf("multiSpeakerEnd") != -1) {
            let speakerHover = +node.dataset.hover
            let speed = +node.dataset.speed
            let pitch = +node.dataset.pitch
            let gender = node.dataset.gender
            let speaker = node.dataset.speaker
            let speakerName = node.dataset.speakername
            let speakerSource = node.dataset.speakersource
            spanObj = {
              $index: i,
              nodeType: node.nodeType,
              wordType: "multiSpeakerEnd",
              word: node.textContent || " ",
              isTag: true,
              $deep: deep,
              isPlaceHolder: 1,
              endSpeaker: true,
              speed: speed,
              pitch: pitch,
              hover: speakerHover,
              gender: gender,
              speaker: speaker,
              speakerName: speakerName,
              speakerSource: speakerSource,
              emotionWeight: node.dataset.emotionweight || 1
            }
            list.push(spanObj)
          } else if (node.className.indexOf("ignore") != -1 && node.className.indexOf("aliasChangeStart") != -1) {
            spanObj = {
              $index: i,
              nodeType: node.nodeType,
              wordType: "aliasChangeStart",
              word: node.textContent.trim() || " ",
              isTag: true,
              $deep: deep,

              aliasChangeStart: true,
              aliasChange: true,
              aliasContent: node.dataset.aliascontent,
              aliasGroupKey: +node.dataset.aliasgroupkey,
              aliasLength: node.dataset.originalias.length,
              originAlias: node.dataset.originalias
              // s_index: 0,
              // index: 1,
            }
            list.push(spanObj)
          } else if (node.className.indexOf("ignore") != -1 && node.className.indexOf("aliasChangeEnd") != -1) {
            spanObj = {
              $index: i,
              nodeType: node.nodeType,
              wordType: "aliasChangeEnd",
              word: node.textContent.trim() || " ",
              isTag: true,
              $deep: deep,

              aliasChangeEnd: true,
              aliasChange: true,
              aliasContent: node.dataset.aliascontent,
              aliasGroupKey: +node.dataset.aliasgroupkey,
              aliasLength: node.dataset.originalias.length,
              originAlias: node.dataset.originalias
              // s_index: 0,
              // index: 1,
            }
            list.push(spanObj)
          }else if (node.className.indexOf("ignore") != -1 && node.className.indexOf("phoneticsStart") != -1) {
            spanObj = {
              $index: i,
              nodeType: node.nodeType,
              wordType: "phoneticsStart",
              word: " ",
              isTag: true,
              $deep: deep,
              pronChangeStart: true,
              pronChange: true,
              pronContent: node.dataset.phoneticstext,
              originPron: node.dataset.originpron,
              pronLength: node.dataset.originpron.length,
              phoneticsId: node.dataset.phoneticsid
              // s_index: 0,
              // index: 1,
            }
            list.push(spanObj)
          } else if (node.className.indexOf("ignore") != -1 && node.className.indexOf("phoneticsEnd") != -1) {
            spanObj = {
              $index: i,
              nodeType: node.nodeType,
              wordType: "phoneticsEnd",
              word: " ",
              isTag: true,
              $deep: deep,
              pronChangeStart: true,
              pronChange: true,
              pronContent: node.dataset.phoneticstext,
              originPron: node.dataset.originpron,
              pronLength: node.dataset.originpron.length,
              phoneticsId: node.dataset.phoneticsid
              // s_index: 0,
              // index: 1,
            }
            list.push(spanObj)
          } else if(node.className.indexOf("ignore")!=-1&&!node.textContent.trim()) {
            if($(node).find(".oldcaret").length>=1||node.className=="oldcaret"){
              if(caret==-1){
                list.push("caret")
                caret=1
              }
            }
            continue
          } else if (node.contentEditable == "false") {
            if (node.getAttribute("data-type") == "multi-tone" && node.textContent.trim()) {
              // 多音字
              const pinyin = node.getAttribute("data-value") || ""
              const originPinyin = node.getAttribute("data-origin-pinyin") || ""
              spanObj = {
                $index: i,
                nodeType: node.nodeType,
                wordType: "multi-tone",
                word: node.textContent || " ",
                text: node.textContent || " ",
                isChange: pinyin !== originPinyin,
                pinyin,
                originPinyin,
                $deep: deep,
              }
              list.push(spanObj)
            } else if (node.getAttribute("data-type") == "pause") {
              spanObj = {
                $index: i,
                nodeType: node.nodeType,
                wordType: "pause",
                word: " ",
                isTag: true,
                rhythmData: true,
                rhythm: node.getAttribute("data-value"),
                $deep: deep,
              }
              list.push(spanObj)
            } else if (node.getAttribute("data-type") == "mute") {
              // 静音
              let obj: any = {
                $index: i,
                nodeType: node.nodeType,
                wordType: "mute",
                isTag: true,
                isPlaceHolder: true,
                word: " ",
                digital: node.getAttribute("data-value"),
                isMute: true,
                $deep: deep,
              }
            
              spanObj = obj
              list.push(spanObj)
            } else if (node.getAttribute("data-type") == "globalMute") {
              let value=$(node).find(".effect-mute").attr("data-value")
              let isSelf=$(node).hasClass("isSelf")
              // 静音
              let obj: any = {
                $index: i,
                nodeType: node.nodeType,
                digital: value,
                isMute: true,
                isTag: true,
                wordType: "mute",
                originPinyin: "",
                originRhythm: 0,
                pinyin: "",
                rhythm: 0,
                text_attr: "space",
                muteChange:isSelf,
                isGlobalMute: true,
                word: " ",
                $deep: deep,
              }
              spanObj = obj
              list.push(spanObj)
            } else if (node.getAttribute("data-type") == "stress") {
              // 重读
              const stresshover = hoverCounter++ // pc hover用
              const valueObj = {
                "1": {// 重读
                  stresshover,
                  isStress: 2
                },
                "2": {// 拖音
                  stresshover,
                  dragSound: 2
                },
                "3": {// 重+拖
                  stresshover,
                  stressAndDragSound: 2
                },
              }[node.getAttribute('data-value')]
              const boundObj = {
                $index: i,
                nodeType: node.nodeType,
                word: " ",
                isTag: true,
                ...valueObj,
                $deep: deep,
                wordType: 'stress'
              }
              list.push({
                ...boundObj,
                startStressRead: true,
                wordType: 'stressStart',
                isTag: true
              });
              [...node.childNodes].forEach(node => {
                const wordObj = {
                  $index: i,
                  nodeType: node.nodeType,
                  wordType: "stress",
                  word: node.textContent|| " ",
                  text: node.textContent|| " ",
                  ...valueObj,
                  $deep: deep
                }
                list.push(wordObj)
              })
              list.push({
                ...boundObj,
                endStressRead: true,
                wordType: 'stressEnd',
                isTag: true
              });
            }
          } else {
            if (node.childNodes && node.childNodes.length > 0 ) {
              list = this.getChildNodesAst(node, this.deep, list)
            } else {
              if (
                node.textContent.trim()!=="" &&
                  node.getAttribute('data-type') !== 'stress-read'
              ) {
                spanObj = {
                  $index: i,
                  nodeType: node.nodeType,
                  text: node.textContent.trim(),
                  $deep: deep,
                }
                list.push(spanObj)
              }
            }
          }

          break
        case "DIV":
          let dataset = node.dataset
          // if()
          console.log(dataset)
          let data: any = {
            $index: i,
            nodeType: node.nodeType,
            $deep: deep,
          }
          if (dataset.type || node.contentEditable == "false") {
            data.$isEffect = true
            data = {
              ...dataset,
            }

            list.push(data)
          } else {
            // if(deep > 1) {
              this.deep = this.deep + 1
            list = this.getChildNodesAst(node, this.deep, list)
            // } else {
            //     data.children = this.getChildNodesAst(node, deep + 1, list);
            //     list.push(data);
            // }
          }
          break
        default:
          break
      }
    }
    return list
  }

  public static _nodeToMap(node: any) {
    let amp: any = {}

    // console.log(node.nodeType)
    let dataset = node.dataset

    if (dataset?.type) {
      amp.$isEffect = true
      amp.type = dataset.type
      amp.attrs = dataset
    }

    // 回车
    if (node.nodeType == 3 && node.textContent.indexOf("\n") > -1) {
      amp.isEnter = true
    }

    // Element
    if (node.nodeType == 1 && node.textContent) {
      amp.text = node.textContent.trim()
    }

    return amp
  }
  /**
   * 检测节点node在父节点中的索引位置
   * @method getNodeIndex
   * @param { Node } node 需要检测的节点对象
   * @return { Number } 该节点在父节点中的位置
   * @see UE.dom.domUtils.getNodeIndex(Node,Boolean)
   */

  /**
   * 检测节点node在父节点中的索引位置， 根据给定的mergeTextNode参数决定是否要合并多个连续的文本节点为一个节点
   * @method getNodeIndex
   * @param { Node } node 需要检测的节点对象
   * @param { Boolean } mergeTextNode 是否合并多个连续的文本节点为一个节点
   * @return { Number } 该节点在父节点中的位置
   * @example
   * ```javascript
   *
   *      var node = document.createElement("div");
   *
   *      node.appendChild( document.createTextNode( "hello" ) );
   *      node.appendChild( document.createTextNode( "world" ) );
   *      node.appendChild( node = document.createElement( "div" ) );
   *
   *      //output: 2
   *      console.log( UE.dom.domUtils.getNodeIndex( node ) );
   *
   *      //output: 1
   *      console.log( UE.dom.domUtils.getNodeIndex( node, true ) );
   *
   * ```
   */
  public static getNodeIndex (node, ignoreTextNode?) {
    let preNode = node,
        i = 0;
    while ((preNode = preNode.previousSibling)) {
      if (ignoreTextNode && preNode.nodeType == 3) {
        if (preNode.nodeType != preNode.nextSibling.nodeType) {
          i++;
        }
        continue;
      }
      i++;
    }
    return i;
  }

  public static getTagNode(node: HTMLElement) {
    let target = node
    while (node&&node.classList.value.indexOf("isTag") == -1 && node.classList.value.indexOf("tag") == -1) {
      node = node.parentElement
      if (node&&(node.classList.value.indexOf("isTag") || node.classList.value.indexOf("tag"))) {
        target = node
        break
      }
    }
    return target
  }

  public static getElementByAttr(tag, dataAttr, datavlue) {
    let aElements = document.getElementsByTagName(tag);
    let aEle = [];
    for(let i = 0; i < aElements.length; i++) {
      let ele = aElements[i].getAttribute(dataAttr);
      if(datavlue == ele) {
        aEle.push(aElements[i]);
      }
    }
    return aEle;
  }
  public static renderMuteLine(count: number) {
    let str = '<span class="mute-box">'

    function getMuteList(data: number) {
      let result = []
      if (data > 1 && data < 200) {
        result = [1]
      } else if (data >= 200 && data < 300) {
        result = [1, 2]
      } else if (data >= 300) {
        result = [1, 2, 3]
      }
      return result
    }

    getMuteList(count).forEach((item) => {
      str += `<span class="mute-list mute-list-${item}"></span>`
    })

    str += "</span>"
    return str
  }
}
